import { logger } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { Loader } from '@progress/kendo-react-indicators';
import React from 'react';
import { useAppDispatch } from '../../app/hooks/store-hooks';
import { startPlayback } from '../../app/state/reducers/playback';
import { DateTimeFormatter } from '../../formatters';
import { PlayCircleOutline } from '../../icons/icons';
import { getDirectoryDb } from '../../services/directory-db';

type RecordingEntry = {
    id: number;
    name: string;
    dirHandle: FileSystemDirectoryHandle;
    fileHandle: FileSystemFileHandle;
    time: number;
};

export function ReplayList(props: { onClose?: () => void }) {
    const [files, setFiles] = React.useState([] as RecordingEntry[]);
    const [skip, setSkip] = React.useState(0);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const dispatch = useAppDispatch();

    const take = 15;

    const loadItems = async () => {
        try {
            let i = 0;
            const db = await getDirectoryDb();
            const root = await db.get('file-handles', 'recording');
            if (!root || root.kind !== 'directory') {
                return;
            }

            const files = [] as RecordingEntry[];

            for await (const [, dirHandle] of root.entries()) {
                if (dirHandle.kind !== 'directory') {
                    continue;
                }

                for await (const [fileName, fileHandle] of dirHandle.entries()) {
                    if (fileHandle.kind !== 'file') {
                        continue;
                    }

                    if (fileHandle.name.endsWith('.fwrx')) {
                        const file = await fileHandle.getFile();
                        files.push({
                            id: i++,
                            name: fileName,
                            dirHandle: dirHandle,
                            fileHandle: fileHandle,
                            time: file.lastModified / 1000
                        });
                        break;
                    }
                }
            }
            files.sort((a, b) => b.time - a.time);
            setFiles(files);
        } catch (err) {
            logger.trackException(err);
        }
    };

    React.useEffect(() => {
        loadItems();
    }, []);

    const handlePageChange = (event: PageChangeEvent) => {
        setSkip(event.skip);
    };

    const handleLoadPaybackFromFile = async () => {
        try {
            setError('');
            const directoryDb = await getDirectoryDb();
            const rootDir = await directoryDb.get('file-handles', 'recording');
            const result = await window.showDirectoryPicker({ startIn: rootDir });

            if (!result) {
                return;
            }

            setBusy(true);
            const recordingDirectory = result;
            const files = [];
            for await (const fileName of recordingDirectory.keys()) {
                if (!fileName.endsWith('.fwrx')) {
                    continue;
                }
                files.push(fileName);
            }

            if (files.length === 0) {
                setError('No replay file found in the directory.');
                return;
            }

            const fileName = files[0];
            const recordingFileHandle = await recordingDirectory.getFileHandle(fileName, { create: false });

            const resultPlayback = await dispatch(
                startPlayback({ kind: 'file', file: recordingFileHandle, directory: recordingDirectory })
            ).unwrap();
            if (resultPlayback.ok) {
                props.onClose?.();
            } else {
                setError(resultPlayback.error);
            }
        } catch (err) {
            logger.trackException(err);
            setError('Could not load playback file');
        } finally {
            setBusy(false);
        }
    };

    const handlePlay = async (entry: RecordingEntry) => {
        const resultPlayback = await dispatch(startPlayback({ kind: 'file', file: entry.fileHandle, directory: entry.dirHandle })).unwrap();
        if (resultPlayback.ok) {
            props.onClose?.();
        } else {
            setError(resultPlayback.error);
        }
    };

    return (
        <div className="d-flex flex-column gap-2" style={{ height: 'min(80vh, 800px)', width: 'min(80vw, 500px)' }}>
            <div className="text-danger">{error}</div>
            <div>
                {import.meta.env.DEV && (
                    <Button
                        onClick={() => loadItems()}
                        style={{ float: 'right', marginLeft: '0.5rem' }}
                        themeColor={'primary'}
                        fillMode={'outline'}>
                        Refresh
                    </Button>
                )}

                <Button onClick={handleLoadPaybackFromFile} style={{ float: 'right' }} themeColor={'primary'} fillMode={'outline'}>
                    Open replay file
                </Button>
            </div>
            <div className="flex-grow-1 overflow-auto">
                {files.slice(skip, skip + take).map((item) => {
                    return (
                        <div key={item.id} className="d-flex gap-2 align-items-center pt-2 justify-content-between">
                            <div>
                                <strong>{item.name}</strong>
                                <div>{DateTimeFormatter.formatDateTime(item.time)}</div>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    fillMode="outline"
                                    onClick={() => handlePlay(item)}
                                    svgIcon={PlayCircleOutline}
                                    title="Open replay"
                                    themeColor="primary"></Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Pager className="mt-2 pe-2" total={files.length} skip={skip} take={take} onPageChange={handlePageChange}></Pager>

            {busy && (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ position: 'absolute', inset: '0', background: 'rgba(0,0,0,0.5)' }}>
                    <Loader />
                </div>
            )}
        </div>
    );
}
